import removeHyphen from '@/helpers/removeHyphen'
import childEdition from '@/services/http/api/childEdition'
import edition from '@/services/http/api/edition'
import cloneDeep from 'lodash.clonedeep'
import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useGuardianConsentStore } from '@/stores'

type Parent = {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
}

type School = {
  province: string
  wioStructLocationId: number | null
  schoolClass: number
}

type Child = {
  firstName: string
  lastName: string
  birthdate: string
  pesel: string | null
  gender: string
}

type Consent = Record<number, boolean>

// @TODO: discuss and decide on the best way to handle form with vee-validate.

export const useExternalFormStore = defineStore('externalForm', () => {
  const parent = ref<Partial<Parent>>({})

  const child = ref<Partial<Child>>({})

  const school = ref<Partial<School>>({})

  const consent = ref<Consent>({})

  const loader = ref<boolean>(false)

  const isPeselDisabled = ref<boolean>(false)

  const childEditionCreateFormAccess = ref<boolean>(false)

  const store = useGuardianConsentStore()

  const { guardianConsent } = storeToRefs(store)

  const fetchChildEditionCreateFormAccess = async () => {
    try {
      const { data: responseData } = await edition.fetchChildEditionCreateFormAccess()
      childEditionCreateFormAccess.value = responseData?.data?.child_edition_create_form_access
    } catch (error) {
      childEditionCreateFormAccess.value = false
      console.error('[externalForm] Failed to fetch child edition create form access:', error)
    }
  }

  const getChildEditionCreateFormAccess = async () => {
    if (!childEditionCreateFormAccess.value) {
      await fetchChildEditionCreateFormAccess()
    }
    return childEditionCreateFormAccess.value
  }

  const clearForm = () => {
    parent.value = {}
    child.value = {}
    school.value = {}
    consent.value = {}
    isPeselDisabled.value = false
  }

  const preparePayload = () => {
    const { address, parentInfo, childApplication } = guardianConsent.value

    const payload = cloneDeep({
      ...child.value,
      ...school.value,
      guardianConsent: {
        ...address,
        ...parentInfo,
        ...childApplication
      },
      child_edition_guardian: parent.value,
      consents: consent.value
    })

    payload.guardianConsent.postalCode = removeHyphen(payload.guardianConsent.postalCode)
    if (payload.pesel === '') payload.pesel = null

    return payload
  }

  const submit = async () => {
    loader.value = true

    const payload = preparePayload()
    try {
      const response = await childEdition.post(payload)
      return response
    } catch (error) {
      console.error('[externalForm] Failed to submit form:', error)
      throw error
    } finally {
      loader.value = false
    }
  }

  return {
    parent,
    child,
    school,
    consent,
    loader,
    isPeselDisabled,
    childEditionCreateFormAccess,
    guardianConsent,
    submit,
    clearForm,
    fetchChildEditionCreateFormAccess,
    getChildEditionCreateFormAccess
  }
})
